import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/footer.css';
import logoWhite from '../assets/logo-white.png';

type FooterProps = {
    className?: string;
};

type FooterLink = {
    to: string;
    labelKey: string;
};

type FooterSection = {
    titleKey: string;
    links: FooterLink[];
};

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
    const { t, i18n } = useTranslation();
    const [openSection, setOpenSection] = React.useState<number | null>(null);

    const sections: FooterSection[] = [
        {
            titleKey: 'Footer.sections.primary',
            links: [
                { to: '/', labelKey: 'Header.nav.home' },
                { to: '/about', labelKey: 'Header.nav.about' },
                { to: '/ai', labelKey: 'Header.nav.ai' }
            ]
        },
        {
            titleKey: 'Header.nav.services',
            links: [
                { to: '/services/china/enterprise/onestop', labelKey: 'Header.services.items.chinaEnt1' },
                { to: '/services/china/personal/digital', labelKey: 'Header.services.items.chinaPer1' }
            ]
        },
        {
            titleKey: 'Footer.sections.resources',
            links: [
                { to: '/resources/news', labelKey: 'Header.nav.news' },
                { to: '/resources', labelKey: 'Header.nav.resources' },
                { to: '/resources/core-people', labelKey: 'Footer.links.corePeople' }
            ]
        },
        {
            titleKey: 'Header.nav.contact',
            links: [
                { to: '/contact/projects', labelKey: 'Header.contact.items.project' },
                { to: '/contact/join', labelKey: 'Header.contact.items.joinUs' }
            ]
        }
    ];

    const currentLanguage = i18n.language.startsWith('en')
        ? 'English'
        : i18n.language.startsWith('ja')
            ? '\u65e5\u672c\u8a9e'
            : '\u4e2d\u6587';

    return (
        <footer className={`site-footer ${className}`.trim()}>
            <div className="footer-shell">
                <div className="footer-top">
                    <div className="footer-brand">
                        <Link to="/" className="footer-logo" aria-label={t('Footer.logoText')}>
                            <img src={logoWhite} alt="Future World" />
                            <span>{t('Footer.logoText')}</span>
                        </Link>

                        <div className="footer-contact-card footer-contact-card-desktop">
                            <div className="footer-contact-row">
                                <span className="footer-contact-label">{t('Footer.officeLabel')}</span>
                                <span className="footer-contact-value">{t('Footer.officeAddress')}</span>
                            </div>
                            <div className="footer-contact-row">
                                <span className="footer-contact-label">{t('Footer.phoneLabel')}</span>
                                <a className="footer-contact-value" href={`tel:${t('Footer.phone')}`}>
                                    {t('Footer.phone')}
                                </a>
                            </div>
                            <div className="footer-contact-row">
                                <span className="footer-contact-label">{t('Footer.emailLabel')}</span>
                                <a className="footer-contact-value" href={`mailto:${t('Footer.email')}`}>
                                    {t('Footer.email')}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="footer-nav-grid">
                        {sections.map((section, index) => (
                            <div key={section.titleKey} className="footer-nav-group">
                                <button
                                    type="button"
                                    className="footer-nav-trigger"
                                    aria-expanded={openSection === index}
                                    onClick={() => setOpenSection((current) => (current === index ? null : index))}
                                >
                                    <span className="footer-nav-title">{t(section.titleKey)}</span>
                                    <span className="footer-nav-icon" aria-hidden="true">{openSection === index ? '-' : '+'}</span>
                                </button>
                                <ul className={`footer-nav-list${openSection === index ? ' is-open' : ''}`}>
                                    {section.links.map((link) => (
                                        <li key={`${section.titleKey}-${link.to}`}>
                                            <Link to={link.to} className="footer-nav-link">
                                                {t(link.labelKey)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="footer-contact-card footer-contact-card-mobile">
                        <div className="footer-contact-row">
                            <span className="footer-contact-label">{t('Footer.officeLabel')}</span>
                            <span className="footer-contact-value">{t('Footer.officeAddress')}</span>
                        </div>
                        <div className="footer-contact-row">
                            <span className="footer-contact-label">{t('Footer.phoneLabel')}</span>
                            <a className="footer-contact-value" href={`tel:${t('Footer.phone')}`}>
                                {t('Footer.phone')}
                            </a>
                        </div>
                        <div className="footer-contact-row">
                            <span className="footer-contact-label">{t('Footer.emailLabel')}</span>
                            <a className="footer-contact-value" href={`mailto:${t('Footer.email')}`}>
                                {t('Footer.email')}
                            </a>
                        </div>
                    </div>
                </div>

                <div className="footer-bottom">
                    <span>{t('Footer.copyright')}</span>
                    <span>{t('Footer.languageLabel', { language: currentLanguage })}</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
